// Camera passthrough, guest side: capture this browser's camera and push the frames
// to the panel, which relays them to the farm agent feeding the device's virtual
// camera (server/relay.ts → /ws-relay-camera).
//
// Wire format — deliberately the dumbest thing that works everywhere:
//   one binary WS message = one complete JPEG frame.
// The panel never parses the bytes, so this is a contract between THIS file and the
// farm agent's camera sink. MJPEG over canvas costs bandwidth (~1–3 Mbit/s at
// 640×480), but it needs no codec support in the page and works on iOS Safari, which
// WebCodecs does not reliably — and the share link is opened on a phone most of the
// time. Swapping in WebCodecs later means changing this file and the agent's sink.
//
// getUserMedia requires a secure context: over plain http (other than localhost) the
// browser refuses outright, which is why the error is surfaced verbatim to the guest.
import { t } from '../ui/lang';
/**
 * Frame size pushed to the device, picked from the camera's own orientation.
 *
 * What the app on the device shows follows the ASPECT OF THE INJECTED STREAM, not the
 * emulated sensor's 1920×1080 geometry — verified on a live instance: a landscape
 * stream renders as a thin band inside the portrait preview, a portrait one fills the
 * screen. So a phone held upright must send an upright frame.
 */
const LANDSCAPE_FRAME = { width: 1280, height: 720 };
const PORTRAIT_FRAME = { width: 720, height: 1280 };
/** Requested from the camera; the track decides the final orientation. */
const CAPTURE_WIDTH = 1280;
const CAPTURE_HEIGHT = 720;
/** Frames per second pushed to the device. Enough for a liveness/document check. */
const CAPTURE_FPS = 12;
const JPEG_QUALITY = 0.6;
/**
 * Start streaming the guest's camera to `udid`. Resolves once the uplink is open and
 * the first frame is on its way; rejects with a user-readable message when the camera
 * or the socket is unavailable. `onEnd` fires if the uplink drops on its own.
 *
 * `facing` picks the camera; switching it means stopping and starting again, since the
 * frame geometry is fixed for the life of the session (see the note on the frame below).
 */
export async function startGuestCamera(token, udid, onEnd, facing = 'environment') {
    if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error(t('share.camera_needs_https'));
    }
    let stream;
    try {
        stream = await navigator.mediaDevices.getUserMedia({
            // `ideal` everywhere, never `exact`: a phone that can't give 1280×720 (or is
            // held upright, so its track comes out portrait) still opens — the draw step
            // letterboxes whatever arrives into the 16:9 frame the device expects — and a
            // laptop with a single front camera still opens on `environment`.
            video: {
                width: { ideal: CAPTURE_WIDTH },
                height: { ideal: CAPTURE_HEIGHT },
                facingMode: { ideal: facing },
            },
            audio: false,
        });
    }
    catch (e) {
        const name = e.name;
        if (name === 'NotAllowedError')
            throw new Error(t('share.camera_denied'));
        if (name === 'NotFoundError')
            throw new Error(t('share.camera_not_found'));
        throw new Error(t('share.camera_start_failed'));
    }
    const cleanupStream = () => { for (const track of stream.getTracks())
        track.stop(); };
    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    video.playsInline = true;
    try {
        await video.play();
    }
    catch {
        cleanupStream();
        throw new Error(t('share.camera_preview_failed'));
    }
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${location.host}/?action=camera&udid=${encodeURIComponent(udid)}&share=${encodeURIComponent(token)}`);
    ws.binaryType = 'arraybuffer';
    await new Promise((resolve, reject) => {
        ws.onopen = () => resolve();
        // A refused upgrade (revoked link, camera not allowed, device offline) closes
        // the socket without ever opening it.
        ws.onerror = () => reject(new Error(t('share.camera_not_accepted')));
        ws.onclose = () => reject(new Error(t('share.camera_not_accepted')));
    }).catch((e) => { cleanupStream(); throw e; });
    // Match the frame to how the guest is holding their phone — read from the SCREEN,
    // not from the track: a phone held upright still hands out a landscape track
    // (1280×720) in most mobile browsers, so track dimensions say nothing about how
    // the person is holding the device. The track is then centre-cropped into this
    // frame, which is what a portrait video call looks like.
    //
    // Fixed for the whole session: the encoder on the farm agent is started once per
    // stream and cannot take a resolution change mid-flight, so a mid-session rotation
    // is cropped into the orientation the session began with.
    const screenPortrait = window.matchMedia?.('(orientation: portrait)')?.matches
        ?? window.innerHeight >= window.innerWidth;
    const trackPortrait = video.videoHeight > video.videoWidth;
    const frame = screenPortrait || trackPortrait ? PORTRAIT_FRAME : LANDSCAPE_FRAME;
    const canvas = document.createElement('canvas');
    canvas.width = frame.width;
    canvas.height = frame.height;
    const ctx = canvas.getContext('2d');
    let stopped = false;
    const stop = () => {
        if (stopped)
            return;
        stopped = true;
        clearInterval(timer);
        cleanupStream();
        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)
            ws.close(1000);
    };
    // Drop frames rather than queue them: on a phone uplink the socket buffer is the
    // first thing to fill, and a backlog of stale frames is worse than a lower rate.
    const MAX_BUFFERED = 512 * 1024;
    const timer = setInterval(() => {
        if (stopped || ws.readyState !== WebSocket.OPEN || ws.bufferedAmount > MAX_BUFFERED)
            return;
        // Fill the 16:9 frame by centre-cropping (never stretch): a phone held upright
        // hands us a PORTRAIT track, and letterboxing that would leave a thin strip —
        // the device's camera app adds its own bars on top, so the picture ends up
        // tiny. Cropping is what video-call and verification apps do, and the subject
        // is centred anyway; holding the phone sideways keeps the whole frame.
        const vw = video.videoWidth || canvas.width;
        const vh = video.videoHeight || canvas.height;
        const scale = Math.max(canvas.width / vw, canvas.height / vh);
        const dw = Math.round(vw * scale);
        const dh = Math.round(vh * scale);
        ctx.drawImage(video, Math.round((canvas.width - dw) / 2), Math.round((canvas.height - dh) / 2), dw, dh);
        canvas.toBlob((blob) => {
            if (!blob || stopped || ws.readyState !== WebSocket.OPEN)
                return;
            void blob.arrayBuffer().then((buf) => {
                if (!stopped && ws.readyState === WebSocket.OPEN)
                    ws.send(buf);
            });
        }, 'image/jpeg', JPEG_QUALITY);
    }, Math.round(1000 / CAPTURE_FPS));
    // The farm agent reports back on the same socket: it can only tell us the device
    // rejected the camera AFTER the socket is up (it has to set the box's injection
    // first), so a failure arrives as a message, not as a failed connect.
    ws.onmessage = (ev) => {
        if (typeof ev.data !== 'string')
            return;
        try {
            const msg = JSON.parse(ev.data);
            if (msg.type === 'camera-state' && msg.state === 'error') {
                stop();
                onEnd(msg.error ?? t('share.camera_rejected'));
            }
        }
        catch { /* not ours */ }
    };
    ws.onclose = () => { stop(); onEnd(); };
    ws.onerror = () => { stop(); onEnd(); };
    return { stop };
}
